import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const ICON_TONES = {
  blue:    "bg-blue-50    text-blue-600    ring-blue-100",
  indigo:  "bg-indigo-50  text-indigo-600  ring-indigo-100",
  violet:  "bg-violet-50  text-violet-600  ring-violet-100",
  emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  amber:   "bg-amber-50   text-amber-600   ring-amber-100",
  rose:    "bg-rose-50    text-rose-600    ring-rose-100",
  slate:   "bg-slate-100  text-slate-600   ring-slate-200",
}

const money = (cents, currency) =>
  new Intl.NumberFormat(I18n.locale || "fr", {
    style: "currency", currency: currency || "EUR", maximumFractionDigits: 0,
  }).format((cents || 0) / 100)

const Tile = ({ icon, iconTone = "blue", label, value, sub, delta }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 shadow-sm shadow-slate-900/[0.03]">
    <div className="flex items-start justify-between">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`shrink-0 w-7 h-7 grid place-items-center rounded-lg ring-1 ${ICON_TONES[iconTone]}`}>
        <FontAwesomeIcon icon={icon} className="w-3 h-3" />
      </div>
    </div>
    <div className="mt-2 text-[26px] leading-none font-bold tabular-nums text-slate-900">{value}</div>
    {sub && <div className="mt-1.5 text-[11.5px] leading-snug text-slate-500">{sub}</div>}
    {delta != null && (
      <div className={`mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium ${
        delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
        <FontAwesomeIcon icon={delta >= 0 ? "arrow-trend-up" : "arrow-trend-down"} className="w-2.5 h-2.5" />
        {`${delta > 0 ? "+" : ""}${delta} % ${I18n.t("contracts_dashboard.kpis.vs_last_year")}`}
      </div>
    )}
  </div>
)

const KpiStrip = ({ kpis, quality, currency }) => {
  if (!kpis) return null
  const t = (key) => I18n.t(`contracts_dashboard.kpis.${key}`)

  return (
    <div className="px-7 grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Tile icon="briefcase" iconTone="indigo" label={t("portfolio")}
            value={money(kpis.portfolio_value.cents, currency)}
            sub={t("portfolio_hint")} delta={kpis.portfolio_value.delta} />
      <Tile icon="file-invoice-dollar" iconTone="emerald" label={t("billable")}
            value={money(kpis.billable_next_12m.cents, currency)} sub={t("billable_hint")} />
      <Tile icon="file-signature" iconTone="blue" label={t("active")}
            value={kpis.active_contracts.count}
            sub={I18n.t("contracts_dashboard.kpis.open_ended", { count: kpis.active_contracts.open_ended })} />
      <Tile icon="arrow-trend-up" iconTone="violet" label={t("indexation")}
            value={money(kpis.indexation.gain_cents, currency)}
            sub={I18n.t("contracts_dashboard.kpis.indexation_failing", { count: kpis.indexation.failing })} />
      <Tile icon="hourglass-half" iconTone="amber" label={t("expiring")}
            value={kpis.expiring_90d.count} sub={t("expiring_hint")} />
      <Tile icon={quality?.aberrant_count ? "triangle-exclamation" : "circle-check"}
            iconTone={quality?.aberrant_count ? "rose" : "slate"} label={t("to_fix")}
            value={(quality?.aberrant_count || 0) + (quality?.without_billing_plan || 0)}
            sub={t("to_fix_hint")} />
    </div>
  )
}

export default KpiStrip
